import type {
  ClientsDrillResponse,
  LayawaysDrillResponse,
  OrdersDrillRequest,
  OrdersDrillResponse,
  PaymentsDrillResponse,
  ProductLinesDrillResponse,
} from '@contracts';
import { resolvePeriod } from '@/lib/period';
import { authedFetch } from '@/lib/apiClient';
import { BRIDGE_URL as BRIDGE } from '@/lib/config';

/** Construye los params de scope comunes a los drill-downs. */
function scopeParams(req: OrdersDrillRequest): URLSearchParams {
  const { from, to } = resolvePeriod(req.period);
  const params = new URLSearchParams({ periodFrom: from, periodTo: to });
  if (req.companies?.length) params.set('companies', req.companies.join(','));
  if (req.scope?.branches?.length) params.set('branches', req.scope.branches.join(','));
  if (req.scope?.teams?.length) params.set('teams', req.scope.teams.join(','));
  if (req.scope?.groups?.length) params.set('groups', req.scope.groups.join(','));
  if (req.scope?.employees?.length) params.set('employees', req.scope.employees.join(','));
  if (req.scope?.regions?.length) params.set('regions', req.scope.regions.join(','));
  if (req.source) params.set('source', req.source);
  return params;
}

/** Drill-down de PAGOS (widget Transacciones) — `/metrics/payments/list`. */
export async function fetchPayments(req: OrdersDrillRequest): Promise<PaymentsDrillResponse> {
  const res = await authedFetch(`${BRIDGE}/metrics/payments/list?${scopeParams(req).toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener los pagos');
  return body.data as PaymentsDrillResponse;
}

/** Drill-down de PIEZAS por orden (KPI Productos vendidos) — `/metrics/products/list`. */
export async function fetchProductLines(req: OrdersDrillRequest): Promise<ProductLinesDrillResponse> {
  const res = await authedFetch(`${BRIDGE}/metrics/products/list?${scopeParams(req).toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener los productos');
  return body.data as ProductLinesDrillResponse;
}

/** Drill-down de Clientes Registrados / Nuevos / Cabina — `/metrics/clients/list`. */
export async function fetchClients(req: OrdersDrillRequest): Promise<ClientsDrillResponse> {
  const params = scopeParams(req);
  if (req.clientSegment) params.set('segment', req.clientSegment);
  const res = await authedFetch(`${BRIDGE}/metrics/clients/list?${params.toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener los clientes');
  return body.data as ClientsDrillResponse;
}

/** Drill-down de Apartados — `/metrics/layaways/list`. */
export async function fetchLayaways(req: OrdersDrillRequest): Promise<LayawaysDrillResponse> {
  const res = await authedFetch(`${BRIDGE}/metrics/layaways/list?${scopeParams(req).toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener los apartados');
  return body.data as LayawaysDrillResponse;
}

/**
 * Drill-down: la lista de órdenes detrás de un KPI / vendedor / plaza.
 * Igual que `fetchMetric`, el front solo conoce a BridgeHub (`/metrics/orders/list`),
 * nunca a Odoo. Las comisiones vienen ya calculadas del BFF.
 */
export async function fetchOrders(req: OrdersDrillRequest): Promise<OrdersDrillResponse> {
  const { from, to } = resolvePeriod(req.period);
  const params = new URLSearchParams({ periodFrom: from, periodTo: to });
  if (req.companies?.length) params.set('companies', req.companies.join(','));
  if (req.scope?.branches?.length) params.set('branches', req.scope.branches.join(','));
  if (req.scope?.teams?.length) params.set('teams', req.scope.teams.join(','));
  if (req.scope?.groups?.length) params.set('groups', req.scope.groups.join(','));
  if (req.scope?.employees?.length) params.set('employees', req.scope.employees.join(','));
  if (req.scope?.regions?.length) params.set('regions', req.scope.regions.join(','));
  if (req.employeeId != null && Number.isFinite(req.employeeId)) params.set('employeeId', String(req.employeeId));
  if (req.posConfigId != null && Number.isFinite(req.posConfigId)) params.set('posConfigId', String(req.posConfigId));
  if (req.sellerName) params.set('sellerName', req.sellerName);

  const res = await authedFetch(`${BRIDGE}/metrics/orders/list?${params.toString()}`);
  const body = await res.json();
  if (!body?.success) throw new Error(body?.message || 'Error al obtener las órdenes');
  return body.data as OrdersDrillResponse;
}
